import { useBooleanState } from '@cian/react-utils';

import { useCallback, useMemo } from 'react';

import { type SortType } from '@/packages/api-models/common/json_query';
import { useGetHeaderHeight } from '@/packages/hybrid-search-page-layout/hooks/useGetHeaderHeight';
import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useIntersectionObserver } from '@/packages/react-utils/useIntersectionObserver';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { useListingSortSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { useListingLayoutFiltersHeightSelector } from '@/packages/redux-state/store/slices/listingLayout';

import { SortListingButton } from './components/SortListingButton';
import { type IOption } from './utils/Option';
import { getSortOptions } from './utils/mappers';

type HandleItemClick = (item: IOption<string, SortType>) => void;

const useBoundActions = boundActionsFactory(reduxActions => ({
  setSortWithPageReset: reduxActions.jsonQuery.setSortWithPageReset,
}));

export const SortListingButtonContainer = memoWithDisplayName(() => {
  const boundActions = useBoundActions();

  const { data, refetch } = useSearchOffersDesktopQuery();
  const sortOptions = data?.pages[0].sortingProperties.sortOptions;
  const filtersHeight = useListingLayoutFiltersHeightSelector();
  const sort = useListingSortSelector();
  const headerHeight = useGetHeaderHeight();

  const dropdownVisibilityState = useBooleanState();
  const { setFalse: closeDropdown } = dropdownVisibilityState;

  const { elementRef } = useIntersectionObserver<HTMLButtonElement>(
    {
      observerOptions: { rootMargin: `-${filtersHeight + headerHeight}px 0px 0px 0px` },
      onOutViewport() {
        if (dropdownVisibilityState.state) {
          closeDropdown();
        }
      },
    },
    [],
  );

  const sortButtonItems = useMemo(() => getSortOptions(sortOptions || []), [sortOptions]);

  const activeItem = sortButtonItems.find(item => item.value === sort) ?? sortButtonItems[0];

  const handleItemClick = useCallback<HandleItemClick>(
    item => {
      closeDropdown();

      if (item.value !== activeItem.value) {
        boundActions.setSortWithPageReset(item.value);

        refetch();
      }
    },
    [activeItem.value, boundActions, closeDropdown, refetch],
  );

  return (
    <SortListingButton
      activeItem={activeItem}
      dataTestId="sorting-button"
      dropdownVisibilityState={dropdownVisibilityState}
      items={sortButtonItems}
      ref={elementRef}
      onItemClick={handleItemClick}
    />
  );
}, 'SortListingButtonContainer');
